import { useOutletContext, useNavigate, Link } from "react-router-dom";
import { Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth, hasRole } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUSES = ["draft", "published", "live", "completed", "archived"];

export default function EventSettings() {
  const { event, refresh } = useOutletContext();
  const { role } = useAuth();
  const navigate = useNavigate();

  const changeStatus = async (status) => {
    try { await api.put(`/events/${event.id}`, { status }); toast.success("Status updated"); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async () => {
    try { await api.delete(`/events/${event.id}`); toast.success("Event deleted"); navigate("/events"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-foreground">Event settings</h2>
          {hasRole(role, "moderator") && (
            <Button asChild variant="outline" className="rounded-md"><Link to={`/events/${event.id}/edit`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit details</Link></Button>
          )}
        </div>
        <div>
          <Label>Event status</Label>
          <Select value={event.status} onValueChange={changeStatus} disabled={!hasRole(role, "moderator")}>
            <SelectTrigger className="mt-1.5 capitalize max-w-xs" data-testid="settings-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-2">Draft → Published → Live → Completed → Archived</p>
        </div>
      </div>

      {hasRole(role, "org_admin") && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6">
          <h3 className="font-display font-bold text-foreground">Danger zone</h3>
          <p className="text-sm text-muted-foreground mt-1">Deleting an event removes all its sessions, questions, polls, feedback, surveys and reports.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="mt-4 rounded-md" data-testid="delete-event-btn"><Trash2 className="h-4 w-4 mr-1.5" /> Delete event</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this event?</AlertDialogTitle>
                <AlertDialogDescription>This action cannot be undone. All associated data will be permanently removed.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}
